import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { ALL_DIRECTIONS, Direction } from "../direction";
import { DamageType } from "../damageType";
import { type RobotActor, RobotActorState, type SomethingWithState } from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { RobotAI } from "../ai/robotAI";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { XY } from "../xy";
import { RobotCorpse } from "./robotCorpse";
import { randomElement } from "../random.ts";

export class Robot extends Mob implements SomethingWithState, RobotActor {
	static key = "Robot";
	private state: RobotActorState;
	protected ai: RobotAI;

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(Robot.key);
	}

	constructor(xy: XY, state: RobotActorState = RobotActorState.WANDERING) {
		super(xy);
		this.state = state;
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.damageMultipliers.set(DamageType.MELEE, 0.5);
		this.ai = new RobotAI(this);
		this.ai.attackSoundSupplier = () => new Sound(SoundId.MOB_PHISICAL_ATTACK, this.xy);
		this.ai.noiseSoundSupplier = () => new Sound(SoundId.ROBOT_SOUND, this.xy);
		this.ai.powerOnSoundSupplier = () => new Sound(SoundId.ROBOT_POWER_ON, this.xy);
		this.ai.targetAcquiredSoundSupplier = () => new Sound(SoundId.ROBOT_TARGET_ACQUIRED, this.xy);
		this.ai.startAttackModeSoundSupplier = () => new Sound(SoundId.ROBOT_SWORD_ACTIVATE);
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const state = props?.state as RobotActorState | undefined;
		const robot = new Robot(xy, state);
		robot.lookDirection =
			(props?.lookDirection as Direction) || randomElement([Direction.E, Direction.W]) || Direction.E;
		board.putThing(robot);
		return [robot];
	}

	getState(): RobotActorState {
		return this.state;
	}

	setState(state: RobotActorState): void {
		this.state = state;
	}

	generateCorpse(): PhysicalThing | null {
		return new RobotCorpse(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.ROBOT_DIE);
	}

	processMobAction(_board: Board): void {
		this.ai.processTurn();
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), state: this.state, ai: this.ai.serialize() };
	}

	static deserialize(data: any): Robot {
		const m = new Robot(deserializeXY(data.xy), data.state);
		m.restoreMobFields(data);
		m.ai.restoreFrom(data.ai);
		return m;
	}
}
